import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { useStyles } from '@/pages/IntegrationChains/styles';
import { routes } from '@/pages/routes';
import { PageLayout } from '@/shared/components/PageLayout';
import { useGetAllChains } from './services/hooks/use-get-all-chains';
const columns = [
    { value: 'id', label: 'ИД', minWidth: 40 },
    { value: 'guid', label: 'Идентификатор', minWidth: 280 },
    { value: 'code', label: 'Код', minWidth: 260 },
    { value: 'description', label: 'Описание', minWidth: 200 },
];
const rowsPerPageSetting = [10, 30, 50, 100, 1000];
const DEFAULT_PAGE_SIZE = 50;
const PAGE_NUMBER_PARAM = 'pageNumber';
const PAGE_SIZE_PARAM = 'pageSize';
// Пагинация хранится в query-параметрах, чтобы после F5 открывалась та же страница.
// Невалидные значения из адресной строки заменяются значениями по умолчанию.
const parsePagination = (search) => {
    const params = new URLSearchParams(search);
    const pageNumber = Number(params.get(PAGE_NUMBER_PARAM));
    const pageSize = Number(params.get(PAGE_SIZE_PARAM));
    return {
        pageNumber: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1,
        pageSize: rowsPerPageSetting.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    };
};
export const IntegrationChains = () => {
    const classes = useStyles();
    const history = useHistory();
    const { search } = useLocation();
    const { pageNumber, pageSize } = parsePagination(search);
    const { data: chainsList } = useGetAllChains(`?pageSize=${pageSize}&pageNumber=${pageNumber}`);
    const setPagination = (nextPageNumber, nextPageSize, replace = false) => {
        const params = new URLSearchParams(history.location.search);
        params.set(PAGE_NUMBER_PARAM, String(nextPageNumber));
        params.set(PAGE_SIZE_PARAM, String(nextPageSize));
        const location = { search: `?${params.toString()}` };
        if (replace) {
            history.replace(location);
        }
        else {
            history.push(location);
        }
    };
    const totalCount = chainsList?.totalCount ?? 0;
    const totalPages = chainsList?.totalPages ?? 0;
    // После удаления строк или ручной правки URL страница может оказаться за пределами списка.
    useEffect(() => {
        if (totalPages > 0 && pageNumber > totalPages) {
            setPagination(totalPages, pageSize, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages, pageNumber, pageSize]);
    // Черновик номера страницы - применяется по Enter или потере фокуса, а не на каждый символ.
    const [pageDraft, setPageDraft] = useState(String(pageNumber));
    useEffect(() => {
        setPageDraft(String(pageNumber));
    }, [pageNumber]);
    const applyPageDraft = () => {
        const parsed = Number(pageDraft);
        if (!Number.isInteger(parsed)) {
            setPageDraft(String(pageNumber));
            return;
        }
        const nextPage = Math.min(Math.max(parsed, 1), Math.max(totalPages, 1));
        setPageDraft(String(nextPage));
        if (nextPage !== pageNumber) {
            setPagination(nextPage, pageSize);
        }
    };
    const handlePageKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            applyPageDraft();
        }
    };
    const handleChangePage = (_, newPage) => {
        setPagination(newPage + 1, pageSize);
    };
    const handleChangeRowsPerPage = (event) => {
        setPagination(1, Number(event.target.value));
    };
    return (React.createElement(PageLayout, null,
        React.createElement(Box, { height: "75vh" },
            React.createElement(Box, { mb: 1 },
                React.createElement(Typography, { variant: "h4" }, "\u0418\u043D\u0442\u0435\u0433\u0440\u0430\u0446\u0438\u043E\u043D\u043D\u044B\u0435 \u043F\u0440\u043E\u0446\u0435\u0441\u0441\u044B")),
            React.createElement(Box, { mb: 2 },
                React.createElement(Button, { variant: "contained", color: "primary", startIcon: React.createElement(AddCircleIcon, null), onClick: () => history.push(routes.integrationChainAdd) }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C")),
            React.createElement(Paper, { className: classes.tableRoot },
                React.createElement(TableContainer, { className: classes.tableContainer },
                    React.createElement(Table, { stickyHeader: true },
                        React.createElement(TableHead, null,
                            React.createElement(TableRow, null, columns.map((column) => (React.createElement(TableCell, { key: column.value, style: { minWidth: column.minWidth } }, column.label))))),
                        React.createElement(TableBody, null, chainsList?.chainDescriptions?.map((chain) => (React.createElement(TableRow, { className: classes.tableBodyRow, key: chain.guid, hover: true, onClick: () => history.push(`/integration-chain/${chain.guid}`) }, columns.map((column) => (React.createElement(TableCell, { key: column.value }, chain[column.value]))))))))),
                totalCount > 0 && (React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "flex-end", pl: 2 },
                    React.createElement(TextField, { className: classes.pageInput, size: "small", variant: "outlined", type: "number", label: `Страница из ${totalPages}`, inputProps: { min: 1, max: totalPages }, value: pageDraft, onChange: (event) => setPageDraft(event.target.value), onBlur: applyPageDraft, onKeyDown: handlePageKeyDown }),
                    React.createElement(TablePagination, { labelRowsPerPage: 'Cтрок на странице', rowsPerPageOptions: rowsPerPageSetting, component: "div", count: totalCount, rowsPerPage: pageSize, page: pageNumber - 1, onChangePage: handleChangePage, onChangeRowsPerPage: handleChangeRowsPerPage })))))));
};
